import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/contexts/UserContext';

export type FeedbackType = 'bug' | 'feature' | 'improvement' | 'other';

interface CreateFeedbackParams {
  feedbackType: FeedbackType;
  title: string;
  description: string;
  pageUrl?: string;
  screenshotUrl?: string;
}

export const useFeedback = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useUser();

  const submitFeedback = async (params: CreateFeedbackParams) => {
    if (!currentUser?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to submit feedback',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('feedback').insert({
        profile_id: currentUser.id,
        feedback_type: params.feedbackType,
        title: params.title,
        description: params.description,
        page_url: params.pageUrl || window.location.href,
        screenshot_url: params.screenshotUrl,
      });

      if (error) throw error;

      toast({
        title: 'Feedback submitted',
        description: 'Thank you for your feedback! We\'ll review it soon.',
      });

      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit feedback. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { submitFeedback, loading };
};
